import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { AssetDataResult, ServiceProvider } from '../types/dataManager'
import { AssetCore, FetchingStatus, IWizardQuestion } from '../types/structures'
import { DataStructure } from '../types/DataStructure'
import { useDataProvider, useCachedData } from '../context/DataProviderContext'
import { historyService } from '../services/historyService'
import { getWindowRoomAssetInfo } from '../utils/assetValues'
import { formatFrameFinish, formatFrameModel } from '../utils/windowPosition'
import { en } from '../lang/en'
import { it } from '../lang/it'

interface UseAssetDataOptions {
  locale?: 'en' | 'it'
  autoNavigateToLinking?: boolean
}

export const useAssetData = (
  uuid?: string, 
  options: UseAssetDataOptions = {}
): AssetDataResult => {
  const navigate = useNavigate()
  const { 
    cache, 
    resolveAssetProvider, 
    getService
  } = useDataProvider()
  const { getCachedAsset, getCachedAssetCore, getCachedProjectByAsset } = useCachedData()
  
  const { 
    locale = 'it', 
    autoNavigateToLinking = false 
  } = options

  // State
  const [asset, setAsset] = useState<DataStructure | null>(null)
  const [assetCore, setAssetCore] = useState<AssetCore | null>(null)
  const [project, setProject] = useState<AssetDataResult['project']>(null)
  const [provider, setProvider] = useState<ServiceProvider>('not-set')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [fetchingStatus, setFetchingStatus] = useState<FetchingStatus>('none')
  const [isProjectInstaller, setIsProjectInstaller] = useState(false)

  const translations = locale === 'en' ? en : it
  const resolvedUuid = uuid || ''

  // Fetch asset data from service with translation
  const fetchAssetData = useCallback(async (
    uuid: string,
    provider: ServiceProvider,
    translations: { [key: string]: string }
  ): Promise<DataStructure> => {
    const service = getService(provider)
    const [schemaText, valuesAndLockJson] = await service.getUUIDInfo(uuid)

    // Parse the JSON strings
    const schemaData = JSON.parse(schemaText)
    const valuesAndLock = JSON.parse(valuesAndLockJson)

    // Combine the data
    const combinedData = {
      uuid,
      ...schemaData,
      values: valuesAndLock.values,
      lockStatus: valuesAndLock.lockStatus
    }

    // Convert to string for translation
    let combinedJsonString = JSON.stringify(combinedData)

    // Apply translations
    for (const localeKey in translations) {
      if (translations.hasOwnProperty(localeKey)) {
        const regex = new RegExp(localeKey, 'g')
        combinedJsonString = combinedJsonString.replace(regex, translations[localeKey])
      }
    }

    // Parse back and return as DataStructure instance
    const translatedData = JSON.parse(combinedJsonString)
    return DataStructure.fromJSON(translatedData)
  }, [getService])

  // Main data loading function
  const loadAssetData = useCallback(async () => {
    if (!resolvedUuid) {
      setError('No UUID provided for asset loading.')
      setAsset(null)
      setAssetCore(null)
      setProject(null)
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)
    setFetchingStatus('none')

    try {
      // Check cache first - including provider cache
      const cachedAsset = getCachedAsset(resolvedUuid)
      const cachedAssetCore = getCachedAssetCore(resolvedUuid)
      const cachedProjectForAsset = getCachedProjectByAsset(resolvedUuid)
      
      // Check if we have a cached provider for this asset
      const cachedProvider = cache.current.providerMap.get(resolvedUuid)
      const providerTimestamp = cache.current.lastFetched.get(`provider_${resolvedUuid}`)
      const isProviderCacheValid = providerTimestamp && (Date.now() - providerTimestamp < 5 * 60 * 1000) // 5 minutes
      
      let assetProvider: ServiceProvider
      
      if (cachedProvider && isProviderCacheValid) {
        console.debug(`🔄 Using cached provider ${cachedProvider} for UUID ${resolvedUuid}`)
        assetProvider = cachedProvider
        setProvider(assetProvider)
      } else {
        // Resolve provider
        console.debug(`🔍 Resolving provider for UUID ${resolvedUuid} (cached: ${cachedProvider}, valid: ${isProviderCacheValid})`)
        setFetchingStatus('project')
        assetProvider = await resolveAssetProvider(resolvedUuid)
        console.debug(`✅ Resolved provider ${assetProvider} for UUID ${resolvedUuid}`)
        setProvider(assetProvider)
        
        // Cache the provider resolution
        cache.current.providerMap.set(resolvedUuid, assetProvider)
        cache.current.lastFetched.set(`provider_${resolvedUuid}`, Date.now())
      }
      
      // Load cached project immediately if available (even without asset data)
      if (cachedProjectForAsset) {
        console.debug(`📦 Loaded cached project for asset ${resolvedUuid}:`, cachedProjectForAsset.info?.identification)
        setProject(cachedProjectForAsset)
        
        // Check installer permissions using the correct provider
        const service = getService(assetProvider)
        const hasPermission = await service.checkInstallerProjectPermission(resolvedUuid).catch(() => false)
        setIsProjectInstaller(hasPermission)
      }

      if (cachedAsset && cachedAssetCore) {
        setAsset(cachedAsset)
        setAssetCore(cachedAssetCore)
        setIsLoading(false)
        setFetchingStatus('completed')
        return
      }
      
      const service = getService(assetProvider)

      // Fetch project data if available (only if not cached)
      if (!cachedProjectForAsset) {
        try {
          const projectResult = await service.getProjectByUuid(resolvedUuid)
          if (projectResult) {
            setProject(projectResult)
            
            // Cache project with both keys: by project UUID and by asset UUID
            cache.current.projects.set(projectResult.uuid, projectResult)
            cache.current.projects.set(`asset_${resolvedUuid}`, projectResult)
            cache.current.lastFetched.set(`project_${projectResult.uuid}`, Date.now())
            cache.current.lastFetched.set(`project_asset_${resolvedUuid}`, Date.now())
            
            console.debug(`💾 Cached project for asset ${resolvedUuid}:`, projectResult.info?.identification)
            
            // Add to history
            historyService.addProject(
              resolvedUuid,
              projectResult.info.identification,
              projectResult.info.subIdentification
            )
            
            // Check installer permissions
            const hasPermission = await service.checkInstallerProjectPermission(resolvedUuid).catch(() => false)
            setIsProjectInstaller(hasPermission)
          }
        } catch (projectError) {
          console.info('No project found for asset UUID:', resolvedUuid)
        }
      }

      // Fetch asset data
      setFetchingStatus('data')
      try {
        const assetResult = await fetchAssetData(resolvedUuid, assetProvider, translations)
        if (assetResult) {
          setAsset(assetResult)
          
          // Cache asset
          cache.current.assets.set(resolvedUuid, assetResult)
          cache.current.lastFetched.set(`asset_${resolvedUuid}`, Date.now())
          
          // Add to history, keeping enough context to render the entry the same
          // way the project dashboard renders it
          const roomInfo = getWindowRoomAssetInfo(assetResult)
          historyService.addUUID(
            resolvedUuid,
            assetResult.info?.identification,
            assetResult.info?.subIdentification,
            {
              positionNumber: roomInfo.positionNumber,
              floor: roomInfo.floor,
              description: roomInfo.description,
              frameModel: formatFrameModel(roomInfo),
              frameFinish: formatFrameFinish({
                articleDescription: roomInfo.articleDescription,
                width: roomInfo.width,
                height: roomInfo.height
              })
            }
          )
        }
      } catch (assetError) {
        console.warn('Failed to fetch asset data:', assetError)
        // If asset data fails but we have project, navigate to linking
        if (project && autoNavigateToLinking) {
          navigate(`/linking/${resolvedUuid}`)
          return
        }
      }

      // Fetch asset core
      try {
        const assetCoreResult = await service.getAssetCore(resolvedUuid)
        if (assetCoreResult) {
          setAssetCore(assetCoreResult)
          
          // Cache asset core
          cache.current.assetCores.set(resolvedUuid, assetCoreResult)
          cache.current.lastFetched.set(`assetCore_${resolvedUuid}`, Date.now())
        }
      } catch (coreError) {
        console.warn('Failed to fetch asset core, using default:', coreError)
        const defaultCore = {
          status: 'initialized' as const,
          grants: [],
          identifier: undefined,
          subidentifier: undefined
        }
        setAssetCore(defaultCore)
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load asset data'
      setError(errorMessage)
      console.error('Asset data loading error:', err)
    } finally {
      setIsLoading(false)
      setFetchingStatus('completed')
    }
  }, [
    resolvedUuid, 
    resolveAssetProvider, 
    getService, 
    getCachedAsset, 
    getCachedAssetCore,
    getCachedProjectByAsset,
    fetchAssetData, 
    translations, 
    autoNavigateToLinking, 
    navigate
  ])

  // Reload function
  const reloadData = useCallback(async (): Promise<void> => {
    // Clear cache for this asset and its project relationship
    cache.current.assets.delete(resolvedUuid)
    cache.current.assetCores.delete(resolvedUuid)
    cache.current.projects.delete(`asset_${resolvedUuid}`)
    cache.current.providerMap.delete(resolvedUuid)
    cache.current.lastFetched.delete(`asset_${resolvedUuid}`)
    cache.current.lastFetched.delete(`assetCore_${resolvedUuid}`)
    cache.current.lastFetched.delete(`project_asset_${resolvedUuid}`)
    cache.current.lastFetched.delete(`provider_${resolvedUuid}`)
    
    await loadAssetData()
  }, [resolvedUuid, loadAssetData])

  // Get wizard questions
  const getWizardQuestions = useCallback(async (sectionId: string): Promise<IWizardQuestion[]> => {
    if (!asset?.nodes) return []
    const wizardNode = asset.nodes[sectionId]
    if (!wizardNode?.isWizard || !wizardNode?.questions) return []
    return wizardNode.questions
  }, [asset])

  // Load data on mount or UUID change
  useEffect(() => {
    loadAssetData()
  }, [loadAssetData])

  // Create service wrapper with current provider (memoized to update when provider changes)
  const serviceWrapper = useMemo(() => {
    if (provider === 'not-set') {
      console.debug(`⏸️ Provider not set yet, returning null service wrapper`)
      return null
    }
    console.debug(`🔧 Creating service wrapper for provider: ${provider}`)
    return getService(provider)
  }, [provider, getService])

  return {
    asset,
    assetCore,
    project,
    uuid: resolvedUuid,
    provider,
    isLoading,
    error,
    fetchingStatus,
    isProjectInstaller,
    reloadData,
    getWizardQuestions,
    service: serviceWrapper
  }
}